import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = () => {
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  return null;
};

// Add this helper function
const toCamelCaseArray = (data) => {
  if (!data) return [];
  return data?.map(item => ({
    id: item?.id,
    userId: item?.user_id,
    promoName: item?.promo_name,
    promoType: item?.promo_type,
    originalPrice: item?.original_price,
    discountPercentage: item?.discount_percentage,
    discountFixedAmount: item?.discount_fixed_amount,
    finalPrice: item?.final_price,
    discountAmount: item?.discount_amount,
    revenueImpact: item?.revenue_impact,
    expectedSalesIncreasePercentage: item?.expected_sales_increase_percentage,
    projectedUnitsSold: item?.projected_units_sold,
    projectedRevenue: item?.projected_revenue,
    validFrom: item?.valid_from,
    validUntil: item?.valid_until,
    createdAt: item?.created_at,
    updatedAt: item?.updated_at,
  }));
};

export const promoDiscountService = {
  /**
   * Get all promo discount calculations for the authenticated user
   */
  async getAll() {
    try {
      const effectiveUserId = getEffectiveUserId();
      
      let query = supabase
        ?.from('promo_discount_calculations')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      
      if (effectiveUserId) {
        query = query?.eq('user_id', effectiveUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching promo discount calculations:', error);
      throw error;
    }
  },

  /**
   * Create a new promo discount calculation
   */
  async create(calculation) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('promo_discount_calculations')?.insert({
        user_id: user?.id,
        promo_name: calculation?.promoName,
        promo_type: calculation?.promoType,
        original_price: calculation?.originalPrice,
        discount_percentage: calculation?.discountPercentage,
        discount_fixed_amount: calculation?.discountFixedAmount,
        final_price: calculation?.finalPrice,
        discount_amount: calculation?.discountAmount,
        revenue_impact: calculation?.revenueImpact,
        expected_sales_increase_percentage: calculation?.expectedSalesIncreasePercentage,
        projected_units_sold: calculation?.projectedUnitsSold,
        projected_revenue: calculation?.projectedRevenue,
        valid_from: calculation?.validFrom,
        valid_until: calculation?.validUntil,
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      promoName: data?.promo_name,
      promoType: data?.promo_type,
      originalPrice: data?.original_price,
      discountPercentage: data?.discount_percentage,
      discountFixedAmount: data?.discount_fixed_amount,
      finalPrice: data?.final_price,
      discountAmount: data?.discount_amount,
      revenueImpact: data?.revenue_impact,
      expectedSalesIncreasePercentage: data?.expected_sales_increase_percentage,
      projectedUnitsSold: data?.projected_units_sold,
      projectedRevenue: data?.projected_revenue,
      validFrom: data?.valid_from,
      validUntil: data?.valid_until,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
    };
  },

  /**
   * Delete a calculation
   */
  async delete(id) {
    const { error } = await supabase?.from('promo_discount_calculations')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  /**
   * Delete all calculations for the authenticated user
   */
  async deleteAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('promo_discount_calculations')?.delete()?.eq('user_id', user?.id);

    if (error) throw error;
  },
};